import React from 'react'

import {intersection, sumBy} from 'es-toolkit'
import {isEmpty} from 'es-toolkit/compat'
import invariant from 'invariant'

import config from '../../config'

import {useAppDispatch, useAppSelector} from '../../hooks/redux'
import {signOut} from '../../redux/features/auth'

import useSearch from '../../../../commons/src/hooks/use-search'
import {HttpStatusError} from '../../../../commons/src/libs/http-json'
import {
  getEndOfDay,
  getFirstDayOfCurrentMonth,
  getStartOfDay,
  getTodaysDate,
  isBefore,
} from '../../../../commons/src/services/date'
import useSortAttributes, {SortDefinition} from '../../hooks/use-sort-attributes'
import useTableRowFilters, {FilterMap} from '../../hooks/use-table-row-filters'
import {formatDate, formatPrice} from '../../libs/formatter'
import {transformInternalCreditNoteV2, transformInternalOrderInvoiceV2} from '../../libs/order'
import {BooleanMatchingFilter, filterTableRows} from '../../libs/table-row-filter'
import * as internalApi from '../../services/api/internal'
import * as partnerApi from '../../services/api/partner'
import {captureException, captureIgnoredException} from '../../services/logging'
import {createInvoiceV2Search} from '../../services/search'

import {DateRange, DateRangeConfig} from '../../../../commons/src/types'

import refreshIcon from '../../../../commons/src/assets/icons/refresh.svg'

import ActionBar from '../../../../commons/src/components/action-bar/ActionBar'
import Button from '../../../../commons/src/components/button'
import FilterBar from '../../../../commons/src/components/filter-bar'
import Headline from '../../../../commons/src/components/headline'
import Icon from '../../../../commons/src/components/icon'
import Link from '../../../../commons/src/components/link'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import Paragraph from '../../../../commons/src/components/paragraph'
import SearchInputField from '../../../../commons/src/components/search-input-field'
import SplitLayout from '../../../../commons/src/components/split-layout'
import Stack from '../../../../commons/src/components/stack'
import ActionHeaderLayout from '../../components/action-header-layout'
import Container from '../../components/container'
import Table from '../../components/table'
import TableCell from '../../components/table/TableCell'
import TableHeaderCell from '../../components/table/TableHeaderCell'
import TableRow from '../../components/table/TableRow'

import {
  CreditNoteV2RowContent,
  InvoiceV2RowContent,
  OrderCreditNoteV2,
  OrderDetailTab,
  OrderInvoiceV2,
} from '../../order-types'
import ROUTES from '../../routes'
import DateRangePartial from '../date-range-partial'
import BooleanMatchingFilterPartial from '../filters/boolean-matching-filter-partial'

class LoadInvoicesV2Error extends Error {
  constructor(message?: string) {
    super(message || `Loading invoices failed (v2)`)
    this.name = 'LoadInvoicesError V2'
  }
}

const tableColumnWidths = {
  orderNumber: '115px',
  creationDate: 1,
  placementDate: 1,
  totalCustomerPrice: 1,
  invoiceableProduction: 1,
  invoiceableShipping: 1,
  invoiceableAdditionalCosts: 1,
  additionalCostsMinusMargin: 1,
  invoiceableTotal: 1.5,
}

const SORT_ATTRIBUTES: SortDefinition<InvoiceV2RowContent | CreditNoteV2RowContent> = {
  placementDate: {
    asc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({placementDate: a}, {placementDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
  creationDate: {
    asc: ({creationDate: a}, {creationDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? -1 : 1) : -1,
    desc: ({creationDate: a}, {creationDate: b}) =>
      typeof a !== 'undefined' && typeof b !== 'undefined' ? (isBefore(a, b) ? 1 : -1) : -1,
  },
}

const invoicesDateRangeConfig: DateRangeConfig = config.dateRangeConfig.invoices

const initialInvoicesDateRange = {
  from: getFirstDayOfCurrentMonth(),
  to: getTodaysDate(),
}

const initialFilters: FilterMap<InvoiceV2RowContent | CreditNoteV2RowContent> = {
  additionalCost: new BooleanMatchingFilter({
    key: 'hasAdditionalCosts',
    filterValue: undefined,
  }),
}

const statusFilterOptions = {
  undefined: 'Show all',
  true: 'Show only invoices with additional costs',
  false: 'Hide invoices with additional costs',
}

const InvoicesPartialV2 = () => {
  const [invoicesDateRange, setInvoicesDateRange] =
    React.useState<DateRange>(initialInvoicesDateRange)
  const [invoices, setInvoices] = React.useState<OrderInvoiceV2[]>([])
  const [creditNotes, setCreditNotes] = React.useState<OrderCreditNoteV2[]>([])
  const [invoicesError, setInvoicesError] = React.useState('')
  const [isLoadingInvoices, setIsLoadingInvoices] = React.useState(false)

  const dispatch = useAppDispatch()

  const isAdminMode = useAppSelector(state => state.auth.isAdminMode)
  const provider = useAppSelector(state => state.core.provider)
  invariant(provider, 'Provider is missing.')

  const {filters, setFilter} = useTableRowFilters<InvoiceV2RowContent | CreditNoteV2RowContent>(
    initialFilters
  )

  const tableRows = React.useMemo(() => {
    if (isLoadingInvoices || isEmpty(invoices)) {
      return []
    }

    return invoices.map(invoice => ({
      orderNumber: invoice.orderNumber,
      orderId: invoice.orderId,
      placementDate: invoice.orderDate,
      creationDate: invoice.created,
      currency: invoice.amounts.currency,
      totalCustomerPrice: invoice.amounts.net,
      invoiceableProductionPrice: invoice.invoiceable.production,
      invoiceableShippingPrice: invoice.invoiceable.shipping,
      invoiceableAdditionalCostsPrice: invoice.invoiceable.additionalCosts || 0,
      invoiceableTotalPrice: invoice.invoiceable.total,
      hasAdditionalCosts: !!invoice.invoiceable.additionalCosts,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoices])

  const originalCreditNoteTableRows = React.useMemo(() => {
    if (isLoadingInvoices || isEmpty(creditNotes)) {
      return []
    }

    return creditNotes.map(creditNote => ({
      orderNumber: creditNote.orderNumber,
      orderId: creditNote.orderId,
      placementDate: creditNote.orderDate,
      creationDate: creditNote.created,
      currency: creditNote.amounts.currency,
      totalCustomerPrice: creditNote.amounts.net * -1,
      invoiceableProductionPrice: creditNote.invoiceable.production * -1,
      invoiceableShippingPrice: creditNote.invoiceable.shipping * -1,
      invoiceableAdditionalCostsPrice: creditNote.invoiceable.additionalCosts * -1 || 0,
      invoiceableTotalPrice: creditNote.invoiceable.total * -1,
      hasAdditionalCosts: !!creditNote.invoiceable.additionalCosts,
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditNotes])

  const searchableList = React.useMemo(
    () => [...tableRows, ...originalCreditNoteTableRows],
    [tableRows, originalCreditNoteTableRows]
  )

  const {searchResults, setSearchValue, searchValue} = useSearch(
    searchableList,
    createInvoiceV2Search
  )

  const filteredTableRows = React.useMemo(
    () => filterTableRows(tableRows, Object.values(filters)),
    [filters, tableRows]
  )

  const filteredCreditNoteTableRows = React.useMemo(
    () => filterTableRows(originalCreditNoteTableRows, Object.values(filters)),
    [filters, originalCreditNoteTableRows]
  )

  const getTotalInvoices = (items: InvoiceV2RowContent[]) =>
    items.reduce((sum, item) => sum + item.invoiceableTotalPrice, 0)

  const getTotalCreditNotes = (items: CreditNoteV2RowContent[]) =>
    items.reduce((sum, item) => sum + item.invoiceableTotalPrice, 0)

  const [invoiceTableRows, invoicesTotal, invoicesTotalAmountRow] = React.useMemo(() => {
    const rows = intersection(searchResults, filteredTableRows) as InvoiceV2RowContent[]

    const totalAmountRow = {
      orderNumber: '',
      orderId: '',
      placementDate: undefined,
      creationDate: undefined,
      currency: rows[0]?.currency || '',
      totalCustomerPrice: sumBy(rows, r => r.totalCustomerPrice),
      invoiceableProductionPrice: sumBy(rows, r => r.invoiceableProductionPrice),
      invoiceableShippingPrice: sumBy(rows, r => r.invoiceableShippingPrice),
      invoiceableAdditionalCostsPrice: sumBy(rows, r => r.invoiceableAdditionalCostsPrice),
      invoiceableTotalPrice: sumBy(rows, r => r.invoiceableTotalPrice),
      hasAdditionalCosts: false,
    }
    return [rows, getTotalInvoices(rows), totalAmountRow]
  }, [searchResults, filteredTableRows])

  const [creditNoteTableRows, creditNotesTotal, creditNotesTotalAmountRow] = React.useMemo(() => {
    const rows = intersection(
      searchResults,
      filteredCreditNoteTableRows
    ) as CreditNoteV2RowContent[]

    const totalAmountRow = {
      orderNumber: '',
      orderId: '',
      placementDate: undefined,
      creationDate: undefined,
      currency: rows[0]?.currency || '',
      totalCustomerPrice: sumBy(rows, r => r.totalCustomerPrice),
      invoiceableProductionPrice: sumBy(rows, r => r.invoiceableProductionPrice),
      invoiceableShippingPrice: sumBy(rows, r => r.invoiceableShippingPrice),
      invoiceableAdditionalCostsPrice: sumBy(rows, r => r.invoiceableAdditionalCostsPrice),
      invoiceableTotalPrice: sumBy(rows, r => r.invoiceableTotalPrice),
      hasAdditionalCosts: false,
    }

    return [rows, getTotalCreditNotes(rows), totalAmountRow]
  }, [searchResults, filteredCreditNoteTableRows])

  const {
    sortedItems: sortedInvoiceTableRows,
    sortAttribute,
    sortOrder,
    toggleSortBy,
  } = useSortAttributes(invoiceTableRows, SORT_ATTRIBUTES, 'creationDate')

  const {
    sortedItems: sortedCreditNoteTableRows,
    sortAttribute: creditNoteSortAttribute,
    sortOrder: creditNoteSortOrder,
    toggleSortBy: creditNoteToggleSort,
  } = useSortAttributes(creditNoteTableRows, SORT_ATTRIBUTES, 'creationDate')

  const loadInvoicesAndCreditNotes = async () => {
    setIsLoadingInvoices(true)
    try {
      const sanitizedDateRange = {
        from: getStartOfDay(invoicesDateRange.from),
        to: getEndOfDay(invoicesDateRange.to),
      }

      if (isAdminMode) {
        const [invoicesResponse, creditNoteResponse] = await Promise.all([
          internalApi.getInvoicesV2(provider.id, sanitizedDateRange),
          internalApi.getCreditNotesV2(provider.id, sanitizedDateRange),
        ])
        const transformedInvoices = invoicesResponse.map(invoice =>
          transformInternalOrderInvoiceV2(invoice)
        )
        const transformedCreditNotes = creditNoteResponse.map(creditNote =>
          transformInternalCreditNoteV2(creditNote)
        )
        setInvoices(transformedInvoices)
        setCreditNotes(transformedCreditNotes)
      } else {
        const [invoicesResponse, creditNoteResponse] = await Promise.all([
          partnerApi.getInvoicesV2(sanitizedDateRange),
          partnerApi.getCreditNotesV2(sanitizedDateRange),
        ])
        setInvoices(invoicesResponse)
        setCreditNotes(creditNoteResponse)
      }
      setInvoicesError('')
    } catch (error) {
      if (error && error instanceof HttpStatusError && error.response.status === 401) {
        captureIgnoredException(error)
        dispatch(signOut())
      } else {
        captureException(new LoadInvoicesV2Error((error as Error)?.message), 'default', {
          originalError: error,
          dateFrom: invoicesDateRange.from,
          dateto: invoicesDateRange.to,
          providerId: provider?.id,
        })
        setInvoicesError(
          'We are currently unable to load any of your invoices (v2). Please wait a few minutes and then try again.'
        )
      }
    } finally {
      setIsLoadingInvoices(false)
    }
  }

  const getTableEmpty = () => {
    if (invoicesError) {
      return <Paragraph error>{invoicesError}</Paragraph>
    }
    return <Paragraph minor>No invoices found for the specified time range or filters.</Paragraph>
  }

  const renderRow = (
    row: CreditNoteV2RowContent | InvoiceV2RowContent,
    emphasized = false,
    totalPrefix = '',
    showZeroAsEmpty = false
  ) => (
    <TableRow key={row.orderNumber} emphasized={emphasized}>
      <TableCell strong>
        <Link
          href={`${ROUTES.ORDER_DETAIL.replace(':id', row.orderId)}/${OrderDetailTab.DETAILS}`}
          target="_blank"
        >
          {row.orderNumber}
        </Link>
      </TableCell>
      <TableCell align="right">
        <span>{row.placementDate ? formatDate(row.placementDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>{row.creationDate ? formatDate(row.creationDate) : ''}</span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.totalCustomerPrice === 0
            ? ''
            : formatPrice(row.totalCustomerPrice, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.invoiceableProductionPrice === 0
            ? ''
            : formatPrice(row.invoiceableProductionPrice, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.invoiceableShippingPrice === 0
            ? ''
            : formatPrice(row.invoiceableShippingPrice, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right">
        <span>
          {showZeroAsEmpty && row.invoiceableAdditionalCostsPrice === 0
            ? ''
            : formatPrice(row.invoiceableAdditionalCostsPrice, row.currency, 2)}
        </span>
      </TableCell>
      <TableCell align="right" strong>
        <span>
          {totalPrefix}
          {formatPrice(row.invoiceableTotalPrice, row.currency, 2)}
        </span>
      </TableCell>
    </TableRow>
  )

  const renderInvoicesTable = () => (
    <Table
      isLoading={isLoadingInvoices}
      empty={getTableEmpty()}
      header={
        <TableRow>
          <TableHeaderCell width={tableColumnWidths.orderNumber}>Order number</TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.placementDate}
            sort={sortAttribute === 'placementDate' && sortOrder ? sortOrder : undefined}
            onClick={() => toggleSortBy('placementDate')}
            align="right"
          >
            Placement date
          </TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.creationDate}
            sort={sortAttribute === 'creationDate' && sortOrder ? sortOrder : undefined}
            onClick={() => toggleSortBy('creationDate')}
            align="right"
          >
            Invoice date
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.totalCustomerPrice} align="right">
            Total Customer Price
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableProduction} align="right">
            Invoiceable Production
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableShipping} align="right">
            Invoiceable Shipping
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableAdditionalCosts} align="right">
            Invoiceable Additional Costs
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableTotal} align="right">
            Invoiceable Total
          </TableHeaderCell>
        </TableRow>
      }
    >
      {sortedInvoiceTableRows.map(tableRow => renderRow(tableRow))}
      {sortedInvoiceTableRows.length > 0 &&
        renderRow(invoicesTotalAmountRow, true, 'Total: ', true)}
    </Table>
  )

  const getCreditNotesTableEmpty = () => {
    if (invoicesError) {
      return <Paragraph error>{invoicesError}</Paragraph>
    }
    return (
      <Paragraph minor>No credit notes found for the specified time range or filters.</Paragraph>
    )
  }

  const renderCreditNotesTable = () => (
    <Table
      isLoading={isLoadingInvoices}
      empty={getCreditNotesTableEmpty()}
      header={
        <TableRow>
          <TableHeaderCell width={tableColumnWidths.orderNumber}>Order number</TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.creationDate}
            sort={
              creditNoteSortAttribute === 'creationDate' && creditNoteSortOrder
                ? creditNoteSortOrder
                : undefined
            }
            onClick={() => creditNoteToggleSort('creationDate')}
            align="right"
          >
            Placement date
          </TableHeaderCell>
          <TableHeaderCell
            width={tableColumnWidths.placementDate}
            sort={
              creditNoteSortAttribute === 'placementDate' && creditNoteSortOrder
                ? creditNoteSortOrder
                : undefined
            }
            onClick={() => creditNoteToggleSort('placementDate')}
            align="right"
          >
            Refund date
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.totalCustomerPrice} align="right">
            Total Customer Price
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableProduction} align="right">
            Invoiceable Production
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableShipping} align="right">
            Invoiceable Shipping
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableAdditionalCosts} align="right">
            Invoiceable Additional Costs
          </TableHeaderCell>
          <TableHeaderCell width={tableColumnWidths.invoiceableTotal} align="right">
            Invoiceable Total
          </TableHeaderCell>
        </TableRow>
      }
    >
      {sortedCreditNoteTableRows.map(tableRow => renderRow(tableRow))}
      {sortedCreditNoteTableRows.length > 0 &&
        renderRow(creditNotesTotalAmountRow, true, 'Total: ', true)}
    </Table>
  )

  // Note: creditNotesTotal is a negative value (or zero)
  const totalTotal = invoicesTotal + creditNotesTotal

  return (
    <Container>
      <ActionHeaderLayout
        respectPageTabs
        filterBar={
          <Stack space="200">
            <SplitLayout
              verticalAlignment="end"
              left={
                <ActionBar noWrap alignItems="bottom">
                  <DateRangePartial
                    inputLabel="Invoice date"
                    dateRange={invoicesDateRange}
                    dateRangeConfig={invoicesDateRangeConfig}
                    onChange={setInvoicesDateRange}
                    disabled={isLoadingInvoices}
                  />
                  <Button
                    onClick={loadInvoicesAndCreditNotes}
                    icon={isLoadingInvoices ? <LoadingIndicator /> : <Icon source={refreshIcon} />}
                    iconAlign="right"
                    disabled={isLoadingInvoices}
                  >
                    Load
                  </Button>
                </ActionBar>
              }
              right={
                <Stack space="60">
                  <ActionBar>
                    <Headline label="Your Earnings:" className="u-no-margin-bottom" />
                    <Paragraph size="l" strong className="u-no-margin-bottom">
                      {totalTotal > 0 ? formatPrice(totalTotal, provider.currency, 2) : '-'}
                    </Paragraph>
                  </ActionBar>
                  <Paragraph size="xs" minor className="u-no-margin-bottom">
                    For the specified time range
                  </Paragraph>
                </Stack>
              }
            />
            <FilterBar
              filtersLabel="Filter invoices and credit notes by"
              filters={
                <BooleanMatchingFilterPartial
                  label="Additional costs"
                  size="90"
                  options={statusFilterOptions}
                  filterKey="additionalCost"
                  filterMap={filters}
                  tableRows={searchResults}
                  applyFilter={newFilter => setFilter('additionalCost', newFilter)}
                />
              }
              searchBar={
                <SearchInputField
                  size="90"
                  name="order-search"
                  placeholder="Search by order number"
                  value={searchValue}
                  onChange={value => {
                    setSearchValue(value)
                  }}
                />
              }
            />
          </Stack>
        }
      >
        <Stack space="400">
          <Stack>
            <Headline label="Invoices" className="u-no-margin-bottom" />
            {renderInvoicesTable()}
          </Stack>
          <Stack>
            <Headline label="Credit Notes" className="u-no-margin-bottom" />
            {renderCreditNotesTable()}
          </Stack>
        </Stack>
      </ActionHeaderLayout>
    </Container>
  )
}

export default InvoicesPartialV2
